'use client'

import React, { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, Input, SegmentedControl } from '@kiao-voice/ui-kit'
import type { InvoiceType } from '@kiao-voice/core'
import { isValidTaiwanBusinessId } from '@/lib/tw-validation'
import type { InvoiceSettings } from './api-client'

export type InvoiceMode = InvoiceType

/** Controlled form value for the invoice (發票) toggle + fields. */
export interface InvoiceFormValue {
  mode: InvoiceMode
  taxId: string
  companyName: string
  address: string
}

export interface InvoiceFormErrors {
  taxId: boolean
  name: boolean
  address: boolean
}

export const NO_INVOICE_ERRORS: InvoiceFormErrors = { taxId: false, name: false, address: false }

export const EMPTY_INVOICE_FORM: InvoiceFormValue = {
  mode: 'personal',
  taxId: '',
  companyName: '',
  address: '',
}

/** Hydrate the form value from the server's saved settings. */
export function invoiceFormFromSettings(settings: InvoiceSettings): InvoiceFormValue {
  return {
    mode: settings.invoiceType,
    taxId: settings.taxId ?? '',
    companyName: settings.companyName ?? '',
    address: settings.companyAddress ?? '',
  }
}

/** Serialize the form value to the server's settings shape. */
export function invoiceSettingsFromForm(value: InvoiceFormValue): InvoiceSettings {
  if (value.mode === 'company') {
    return {
      invoiceType: 'company',
      taxId: value.taxId.trim(),
      companyName: value.companyName.trim(),
      companyAddress: value.address.trim(),
    }
  }
  return { invoiceType: 'personal', taxId: null, companyName: null, companyAddress: null }
}

/**
 * Validate the company fields (personal needs none). 統一編號 uses the 113 年新制
 * checksum (isValidTaiwanBusinessId) — instant feedback ahead of the server's
 * authoritative check.
 */
export function validateInvoiceForm(value: InvoiceFormValue): InvoiceFormErrors {
  if (value.mode !== 'company') return { ...NO_INVOICE_ERRORS }
  return {
    taxId: !isValidTaiwanBusinessId(value.taxId.trim()),
    name: !value.companyName.trim(),
    address: !value.address.trim(),
  }
}

export function hasInvoiceError(errors: InvoiceFormErrors): boolean {
  return errors.taxId || errors.name || errors.address
}

export interface InvoiceFieldsProps {
  value: InvoiceFormValue
  errors: InvoiceFormErrors
  /** Account email shown in the personal-mode note. */
  email: string | null
  /** Controlled change — the parent owns the value (and clears errors on edit). */
  onChange: (next: InvoiceFormValue) => void
  /** Disable all inputs (e.g. while a save/charge is in flight). */
  disabled?: boolean
}

/**
 * The shared 發票 (invoice) toggle + conditional fields. Presentational +
 * controlled: the parent owns the value, validation timing, and persistence.
 * Reused by the billing-page {@link InvoicePanel} (saved via its own button) and
 * the checkout panel's order summary (saved at submit). Personal = zero fields,
 * invoices go to the account email; Company = 統一編號 + 抬頭 + 地址.
 */
export function InvoiceFields({ value, errors, email, onChange, disabled }: InvoiceFieldsProps) {
  const { t } = useTranslation('web')
  const baseId = useId()
  const taxIdId = `${baseId}-taxid`
  const nameId = `${baseId}-name`
  const addressId = `${baseId}-address`

  const patch = (next: Partial<InvoiceFormValue>) => onChange({ ...value, ...next })

  return (
    <>
      <SegmentedControl
        aria-label={t('billing.invoiceTypeAria')}
        value={value.mode}
        options={[
          { value: 'personal', label: t('billing.invoiceTypePersonal') },
          { value: 'company', label: t('billing.invoiceTypeCompany') },
        ]}
        onValueChange={(mode) => patch({ mode })}
        disabled={disabled}
      />

      {value.mode === 'personal' ? (
        <p className="note" style={{ marginTop: 'var(--gap-md)' }}>
          {t('billing.invoicePersonalNote', { email: email ?? '—' })}
        </p>
      ) : (
        <div style={{ marginTop: 'var(--gap-md)', display: 'grid', gap: 'var(--gap-sm)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-sm)' }}>
            <Field
              label={t('billing.invoiceTaxIdLabel')}
              htmlFor={taxIdId}
              error={errors.taxId ? t('billing.invoiceTaxIdError') : null}
            >
              <Input
                id={taxIdId}
                className="num"
                inputMode="numeric"
                maxLength={8}
                placeholder={t('billing.invoiceTaxIdPlaceholder')}
                value={value.taxId}
                disabled={disabled}
                onChange={(e) => patch({ taxId: e.target.value.replace(/\D/g, '').slice(0, 8) })}
              />
            </Field>
            <Field
              label={t('billing.invoiceCompanyNameLabel')}
              htmlFor={nameId}
              error={errors.name ? t('billing.invoiceCompanyNameError') : null}
            >
              <Input
                id={nameId}
                placeholder={t('billing.invoiceCompanyNamePlaceholder')}
                value={value.companyName}
                disabled={disabled}
                onChange={(e) => patch({ companyName: e.target.value })}
              />
            </Field>
          </div>
          <Field
            label={t('billing.invoiceAddressLabel')}
            htmlFor={addressId}
            error={errors.address ? t('billing.invoiceAddressError') : null}
          >
            <Input
              id={addressId}
              placeholder={t('billing.invoiceAddressPlaceholder')}
              value={value.address}
              disabled={disabled}
              onChange={(e) => patch({ address: e.target.value })}
            />
          </Field>
        </div>
      )}
    </>
  )
}
